import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useParams, Navigate } from 'react-router-dom';
import { ArrowRight, ArrowLeft, Download, Mail, FileText, Star } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { LICENSING_PATENTS, LICENSING_PATENT_BY_SLUG } from '../data/licensingPatents';

const LicensingPatent = () => {
  const { slug } = useParams();
  const patent = LICENSING_PATENT_BY_SLUG[slug];

  if (!patent) {
    // Soft 404 — redirect to /licensing landing.
    return <Navigate to="/licensing" replace />;
  }

  const allSlugs = LICENSING_PATENTS.map((p) => p.slug);
  const idx = allSlugs.indexOf(slug);
  const prevPatent = idx > 0 ? LICENSING_PATENTS[idx - 1] : null;
  const nextPatent = idx < allSlugs.length - 1 ? LICENSING_PATENTS[idx + 1] : null;
  const glossyUrl = patent.glossy || (patent.num === 9 ? '/documents/SALAR_Patent9_Public_Brief.pdf' : null);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CreativeWork',
    name: patent.title,
    identifier: `U.S. Patent Application No. ${patent.applicationNumber}`,
    creator:  { '@type': 'Person',       name: 'Vikrant Salar' },
    provider: { '@type': 'Organization', name: 'SALAR Technologies LLC', url: 'https://www.salartechnologies.com/' },
    url: `https://www.salartechnologies.com/licensing/${patent.slug}`,
    additionalType: 'Patent application — Filed; Patent Pending',
    description: patent.tagline,
    keywords: patent.keywords.join(', '),
  };

  return (
    <div className="min-h-screen bg-blue-950/20">
      <Helmet>
        <title>{patent.metaTitle}</title>
        <meta name="description" content={patent.metaDescription} />
        <meta name="keywords" content={patent.keywords.join(', ')} />
        <meta property="og:title" content={patent.metaTitle} />
        <meta property="og:description" content={patent.metaDescription} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={`https://www.salartechnologies.com/licensing/${patent.slug}`} />
        <link rel="canonical" href={`https://www.salartechnologies.com/licensing/${patent.slug}`} />
        <meta name="robots" content="index, follow" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      {/* ===== HERO ===== */}
      <section className="bg-gradient-to-b from-[#0a1628] via-[#0e1f3a] to-[#0a1628] border-b border-blue-800/40 py-14 lg:py-20">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <Link to="/licensing" className="inline-flex items-center text-sm text-cyan-300 hover:text-cyan-200 mb-6" data-testid="licensing-back-to-portfolio">
              <ArrowLeft size={14} className="mr-1" /> Back to Patent Portfolio
            </Link>
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <Badge variant="outline" className="border-cyan-400 text-cyan-200 bg-cyan-500/10">
                Patent {patent.num}
              </Badge>
              <Badge variant="outline" className="border-blue-400/60 text-blue-200 bg-blue-500/10 font-mono">
                {patent.applicationNumber}
              </Badge>
              <Badge variant="outline" className="border-emerald-400/50 text-emerald-200 bg-emerald-500/10">
                Filed · Patent Pending
              </Badge>
              {patent.flagship && (
                <Badge variant="outline" className="border-amber-400 text-amber-200 bg-amber-500/10">
                  <Star size={11} className="mr-1 inline" /> FLAGSHIP
                </Badge>
              )}
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight mb-4">
              {patent.title}
            </h1>
            <p className="text-base lg:text-lg text-blue-100/90 leading-relaxed max-w-3xl">
              {patent.tagline}
            </p>
            <div className="flex flex-wrap gap-3 mt-8">
              <Link to={`/licensing/request?patent=${patent.slug}`} data-testid="licensing-patent-cta-request">
                <Button className="bg-cyan-500 hover:bg-cyan-400 text-[#0a1628] font-bold">
                  Request Licensing Brief <ArrowRight className="ml-2" size={16} />
                </Button>
              </Link>
              {glossyUrl && (
                <a href={glossyUrl} target="_blank" rel="noopener noreferrer" data-testid="licensing-patent-cta-glossy">
                  <Button variant="outline" className="border-cyan-400 text-cyan-200 hover:bg-cyan-400/10">
                    <Download className="mr-2" size={16} /> Download 1-Page Glossy
                  </Button>
                </a>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ===== CONTENT ===== */}
      <section className="py-14">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto space-y-8">

            <Card className="border-blue-700/40 bg-blue-950/30">
              <CardHeader>
                <CardTitle className="text-cyan-300 text-xs font-bold tracking-widest uppercase">The Problem</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-blue-100 leading-relaxed">{patent.theProblem}</p>
              </CardContent>
            </Card>

            <Card className="border-blue-700/40 bg-blue-950/30">
              <CardHeader>
                <CardTitle className="text-cyan-300 text-xs font-bold tracking-widest uppercase">What It Does</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-blue-100 leading-relaxed">{patent.whatItDoes}</p>
                <p className="text-xs text-blue-300/60 italic mt-3">
                  This is a public-safe summary. Implementation specifics, claim scope, and architecture detail are in
                  the full Licensing Brief — delivered by email after a one-page mutual NDA.
                </p>
              </CardContent>
            </Card>

            <Card className="border-blue-700/40 bg-blue-950/30">
              <CardHeader>
                <CardTitle className="text-cyan-300 text-xs font-bold tracking-widest uppercase">Who Should License This</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {patent.whoLicenses.map((entry) => (
                    <li key={entry} className="flex items-start gap-2 text-blue-100">
                      <span className="text-cyan-300 mt-1">▸</span>
                      <span>{entry}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            <Card className="border-blue-700/40 bg-blue-950/30">
              <CardHeader>
                <CardTitle className="text-cyan-300 text-xs font-bold tracking-widest uppercase">Why Now</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-blue-100 leading-relaxed">{patent.whyNow}</p>
              </CardContent>
            </Card>

            {/* CTAs */}
            <Card className="border-cyan-400/40 bg-gradient-to-br from-[#0a1628] via-[#0e1f3a] to-[#0a1628]">
              <CardContent className="py-8 text-center">
                <h3 className="text-2xl font-bold text-white mb-2">Get the full Licensing Brief</h3>
                <p className="text-blue-100/90 leading-relaxed mb-6 max-w-2xl mx-auto">
                  The 9–10 page brief covers commercial structure, target verticals, technical architecture, and
                  example deal shapes. It is NDA-only and delivered by email within two business days.
                </p>
                <div className="flex flex-wrap justify-center gap-3">
                  <Link to={`/licensing/request?patent=${patent.slug}`} data-testid="licensing-patent-cta-request-mid">
                    <Button className="bg-cyan-500 hover:bg-cyan-400 text-[#0a1628] font-bold">
                      Request the Brief <ArrowRight className="ml-2" size={16} />
                    </Button>
                  </Link>
                  <a href="mailto:licensing@salartechnologies.com" data-testid="licensing-patent-cta-email">
                    <Button variant="outline" className="border-cyan-400 text-cyan-200 hover:bg-cyan-400/10">
                      <Mail className="mr-2" size={16} /> Email licensing@
                    </Button>
                  </a>
                </div>
              </CardContent>
            </Card>

            {/* Prev / Next */}
            <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
              {prevPatent ? (
                <Link
                  to={`/licensing/${prevPatent.slug}`}
                  className="inline-flex items-center text-sm text-cyan-300 hover:text-cyan-200"
                  data-testid="licensing-patent-prev"
                >
                  <ArrowLeft size={14} className="mr-1" /> P{prevPatent.num} · {prevPatent.shortTitle}
                </Link>
              ) : <span />}
              {nextPatent ? (
                <Link
                  to={`/licensing/${nextPatent.slug}`}
                  className="inline-flex items-center text-sm text-cyan-300 hover:text-cyan-200 ml-auto"
                  data-testid="licensing-patent-next"
                >
                  P{nextPatent.num} · {nextPatent.shortTitle} <ArrowRight size={14} className="ml-1" />
                </Link>
              ) : <span />}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LicensingPatent;
